import React, { useCallback, useMemo, useState } from "react";
import TableContainer from "components/common/TableContainer";
import { ColumnDef } from "@tanstack/react-table";
import { CodeCell, IndexColumn } from "components/common/columns";
import {
  downloadPayrollSlipPdf,
  getPayrollSlips,
} from "../services/PayrollService";
import type { PayrollSlip } from "../types/payroll.types";
import StatusBadge from "components/common/StatusBadge";
import { payrollRunStatusMap } from "../constants/payroll.constants";
import { Link } from "react-router-dom";
import { Button, Input, Spinner } from "reactstrap";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { getEmployeeOptions } from "features/Employees/services/EmployeeService";
import { formatCurrency } from "utils/currency";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

function formatMonthYear(month: number, year: number): string {
  return new Date(year, month - 1, 1).toLocaleString("default", {
    month: "long",
    year: "numeric",
  });
}

async function triggerPdfDownload(slip: PayrollSlip): Promise<void> {
  const blob = await downloadPayrollSlipPdf(slip.id);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `payslip-${slip.id}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export const PayslipsTable = () => {
  const [month, setMonth] = useState<number | undefined>(undefined);
  const [year, setYear] = useState<number | undefined>(
    new Date().getFullYear(),
  );
  const [employeeId, setEmployeeId] = useState<number | undefined>(undefined);
  const [downloading, setDownloading] = useState<number | null>(null);

  const handleDownload = useCallback(async (slip: PayrollSlip) => {
    setDownloading(slip.id);
    try {
      await triggerPdfDownload(slip);
    } finally {
      setDownloading(null);
    }
  }, []);

  const fetchApi = useCallback(
    async ({
      page,
      limit,
    }: {
      page: number;
      limit: number;
      search?: string;
    }) => {
      const res = await getPayrollSlips({
        page,
        limit,
        month,
        year,
        employeeId,
      });
      return {
        success: res.success,
        message: res.message,
        data: res.data ?? {
          rows: [] as PayrollSlip[],
          totalItems: 0,
          currentPage: page,
          pageSize: limit,
          limit,
          totalPages: 0,
        },
      };
    },
    [month, year, employeeId],
  );

  const columns = useMemo<ColumnDef<PayrollSlip>[]>(
    () => [
      IndexColumn<PayrollSlip>(),
      {
        header: "Employee",
        id: "employee",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const slip = row.original;
          const name = `${slip?.employee?.user?.firstName} ${slip?.employee?.user?.lastName}`;
          return (
            <span className="fw-medium">
              {name ?? `Employee #${slip.employeeId}`}
            </span>
          );
        },
      },
      {
        header: "Period",
        id: "period",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const { month, year } = row.original;
          return <span>{formatMonthYear(month, year)}</span>;
        },
      },
      {
        header: "Payroll Run",
        id: "payrollRun",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const run = row.original.payrollItem?.payrollRun;
          if (!run) return <span className="text-muted">—</span>;
          return (
            <Link to={`/finance/payroll/${run.id}`} className="text-body">
              <CodeCell code={run.code ?? `#${run.id}`} />
            </Link>
          );
        },
      },
      {
        header: "Gross",
        id: "gross",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const v = row.original.payrollItem?.grossAmount;
          return <span>{formatCurrency(v)}</span>;
        },
      },
      {
        header: "Net Pay",
        id: "net",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const v = row.original.payrollItem?.netAmount;
          return (
            <span className="fw-medium text-success">{formatCurrency(v)}</span>
          );
        },
      },
      {
        header: "Status",
        id: "status",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const s = row.original.payrollItem?.status;
          if (!s) return <span className="text-muted">—</span>;
          return (
            <StatusBadge value={s} map={payrollRunStatusMap} />
          );
        },
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const slip = row.original;
          const busy = downloading === slip.id;
          return (
            <Button
              size="sm"
              color="soft-primary"
              disabled={busy}
              onClick={() => handleDownload(slip)}
              title="Download payslip PDF"
            >
              {busy ? (
                <Spinner size="sm" />
              ) : (
                <i className="ri-download-2-line align-middle" />
              )}
            </Button>
          );
        },
      },
    ],
    [downloading, handleDownload],
  );

  return (
    <TableContainer<PayrollSlip>
      columns={columns}
      fetchApi={fetchApi}
      pageSize={10}
      globalSearch={false}
      divClass="table-responsive table-card mb-3"
      tableClass="align-middle table-nowrap mb-0"
      theadClass="table-light text-muted"
      toolbarRight={
        <div className="d-flex align-items-center gap-2">
          <div style={{ width: 200 }}>
            <AsyncPaginatedSelect
              fetchOptions={getEmployeeOptions}
              placeholder="All employees"
              value={employeeId ?? null}
              onChange={(opt: any) => setEmployeeId(opt?.value ?? undefined)}
              menuPlacement="bottom"
            />
          </div>
          <Input
            type="select"
            style={{ width: 130 }}
            value={month ?? ""}
            onChange={(e) =>
              setMonth(e.target.value ? Number(e.target.value) : undefined)
            }
            aria-label="Month"
          >
            <option value="">All months</option>
            {MONTHS.map((name, i) => (
              <option key={i + 1} value={i + 1}>
                {name}
              </option>
            ))}
          </Input>
          <Input
            type="select"
            style={{ width: 100 }}
            value={year ?? ""}
            onChange={(e) =>
              setYear(e.target.value ? Number(e.target.value) : undefined)
            }
            aria-label="Year"
          >
            <option value="">All years</option>
            {YEAR_OPTIONS.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Input>
        </div>
      }
    />
  );
};
